import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import { Parser } from 'html-to-react';
import { createSearchParams, Path } from 'react-router';

import { CURRENCY_SIGN } from '@/constants/countries';
import {
  EMAIL_PATTERN,
  MIME_IMAGE_PATTERN,
  MIME_PDF_PATTERN,
  OBJECT_FIELD_PATH_PATTERN,
} from '@/constants/patterns';
import { ADDRESS_PART_ORDER, ADDRESS_PARTS } from '@/constants/places';

import { Language } from '@/types/i18n';
import { User } from '@/types/user';

import { isNil } from './common';
import { i18nCountryTranslator } from './countryTranslations';

export function getAvatarLetters(name: string) {
  if (!name || name.length === 1) return name;

  const words = name.trim().split(/\s+/);

  if (words.length === 1) {
    return `${name[0]}${name[1]}`;
  }

  return `${words[0][0]}${words[1][0]}`;
}

export function getNavigateObject(
  path: string,
  params?: {
    id?: string | number;
    query?: Record<string, string>;
  },
): Partial<Path> {
  return {
    pathname: `${path}${params?.id ? '/' + params?.id : ''}`,
    ...(params?.query
      ? { search: createSearchParams(params?.query).toString() }
      : {}),
  };
}

export function censorEmail(email: string) {
  if (new RegExp(EMAIL_PATTERN).test(email)) {
    const [name, host] = email.split('@');

    if (name.length < 3) {
      return `***@${host}`;
    }

    return `${name.slice(0, 2)}***@${host}`;
  }

  return '';
}

export function censorPhone(code: string, phoneNumber: string) {
  const fullCode = code.startsWith('+') ? code : `+${code}`;

  return `${fullCode} *** ${phoneNumber.slice(-3)}`;
}

export const getFullName = <
  T extends
    | {
        fullName?: string;
        firstName?: string;
        middleName?: string;
        lastName?: string;
      }
    | null
    | undefined,
>(
  data: T,
  options: {
    middleNameAbbreviation?: boolean;
  } = {},
) => {
  const { middleNameAbbreviation = false } = options;

  if (!data) return '';

  if (data.fullName) return data.fullName;

  if (data.firstName && data.middleName && data.lastName) {
    const middleName = middleNameAbbreviation
      ? `${data.middleName.charAt(0).toUpperCase()}.`
      : data.middleName;
    return `${data.firstName} ${middleName} ${data.lastName}`;
  }

  if (data.firstName && data.lastName)
    return `${data.firstName} ${data.lastName}`;

  if (data.firstName) return data.firstName;
  if (data.lastName) return data.lastName;
  if (data.middleName) return data.middleName;

  return '';
};

const htmlParser = Parser();
export const renderHTMLText = (htmlText: string) => {
  return htmlParser.parse(DOMPurify.sanitize(htmlText));
};

export function isRichText(value?: string): value is string {
  return !!value?.trimStart().startsWith('<');
}

export function getDisplayedAddress(user: User) {
  if (!user.addressDetailed) return '';
  const parts = Object.entries(user.addressDetailed)
    .filter(([key, value]) => !!value && ADDRESS_PARTS.includes(key))
    .map(([, value]) => value)
    .sort(
      (a, b) =>
        (ADDRESS_PART_ORDER[a as keyof typeof ADDRESS_PART_ORDER] || 0) -
        (ADDRESS_PART_ORDER[b as keyof typeof ADDRESS_PART_ORDER] || 0),
    );

  if (user.addressDetailed.country) {
    const country = user.addressDetailed.country;
    parts.push(
      i18nCountryTranslator.getCountryName(
        country,
        user.language as Language,
      ) ?? country,
    );
  }

  if (!parts.length) return '';

  return parts.join(', ') || '';
}

export function getDisplayedPhone(phone?: string) {
  if (!phone) return '';

  return phone.startsWith('+') ? phone : `+${phone}`;
}

export function formatDateValue(value: string, template: string) {
  if (!value) return '';

  const parts = value.split('-');
  if (parts.length < 3) return '';

  const [year, month, day] = parts;
  if (year.length !== 4 || month.length !== 2 || day.length !== 2) return '';

  return template
    .replaceAll('dd', day)
    .replaceAll('MM', month)
    .replaceAll('yyyy', year);
}

export function formatDate(
  timestamp: Date | string | number,
  template: string,
) {
  if (!timestamp) return '';

  const original = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const isoDate = new Date(
    original.valueOf() + original.getTimezoneOffset() * 60 * 1000,
  );

  try {
    return format(isoDate, template);
  } catch {
    return '';
  }
}

const DEFAULT_FILENAME = 'document';

export function getFileNameFromUrl(url: string = '') {
  const [urlWithoutParams, searchParams] = url.split('?');
  const lastPathSegment = urlWithoutParams.split('/').pop() || '';
  if (lastPathSegment.includes('.')) {
    return lastPathSegment;
  }
  if (!searchParams) {
    return DEFAULT_FILENAME;
  }
  const params = new URLSearchParams(searchParams);
  const fileNameParam = params.get('fileName');
  if (fileNameParam) {
    return fileNameParam;
  }
  return DEFAULT_FILENAME;
}

export function getObjectFieldByPath(
  object: Record<string, unknown> | undefined,
  keyPath: string,
) {
  if (!object || !new RegExp(OBJECT_FIELD_PATH_PATTERN).test(keyPath)) {
    return undefined;
  }

  try {
    const levels = keyPath.split('.');
    let value = object[levels[0]];

    for (let i = 1; i < levels.length; i++) {
      if (isNil(value) || typeof value !== 'object') return undefined;

      value = (value as Record<string, unknown>)?.[levels[i]];
    }

    return value;
  } catch (e) {
    console.error(e);
    return undefined;
  }
}

export function getPhoneString(countryCode: string, phoneNumber: string) {
  return `${countryCode.replace('+', '')}${phoneNumber}`;
}

export function getCurrencySign(currency: string) {
  return CURRENCY_SIGN[currency] || currency;
}

export function isImageContentType(contentType?: string) {
  if (!contentType) return false;

  return new RegExp(MIME_IMAGE_PATTERN, 'g').test(contentType);
}

export function isPDFContentType(contentType?: string) {
  if (!contentType) return false;

  return new RegExp(MIME_PDF_PATTERN, 'g').test(contentType);
}

export function getAddressLineFromDetailed(addressDetailed?: {
  city?: string;
  state?: string;
  zip?: string;
}): string {
  if (!addressDetailed) return '';
  const parts = [
    addressDetailed.city,
    addressDetailed.state,
    addressDetailed.zip,
  ]
    .map(part => (part || '').trim())
    .filter(Boolean);
  return parts.join(', ');
}

export function convertToBoolean(
  value?: string | null | boolean,
): boolean | undefined {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
}

export function nullIfEmptyString(value?: string | null): string | null {
  if (value === '') return null;
  return value ?? null;
}

export function emptyStringIfNull(value?: string | null): string {
  if (value === null) return '';
  return value ?? '';
}

export const sortStringArray = (a: string, b: string) => {
  return a.localeCompare(b);
};

export function maskName(name: string): string {
  if (!name) return '';
  if (name.length <= 1) return name;
  return name[0] + '*'.repeat(name.length - 1);
}
